import React, { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import original from 'react95/dist/themes/original';
import blue from 'react95/dist/themes/blue';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  Fieldset,
  GroupBox,
  Separator,
  Toolbar,
  Select
} from 'react95';
import ClientDetailsForm from './ClientDetailsForm';
import FinancialSummary from './FinancialSummary';
import TransactionHistory from './TransactionHistory';
import NavigatorPanel from './NavigatorPanel';
// payment arrange
import PaymentArrangementForm from './PaymentArrangementForm';
import { useClients, useDebtAccounts, usePaymentArrangements, useTransactions } from '../hooks';
import { createClient } from '../models/Client';
import { createDebtAccount } from '../models/DebtAccount';
import { createPaymentArrangement, PAYMENT_FREQUENCIES } from '../models/PaymentArrangement';
import { 
  createPaymentTransaction, 
  createAdjustmentTransaction, 
  createContactTransaction,
  createArrangementTransaction,
  CONTACT_TYPES 
} from '../models/TransactionHistory';
import { STORAGE_KEYS } from '../models/constants';

/**
 * Integrated Loan Management Demo
 * 
 * This demo combines all the loan management components to create a complete
 * working interface where you can:
 * - Select and manage clients
 * - View financial summaries with real-time balance calculations
 * - Add payments and see immediate updates across all components
 * - View complete transaction history and audit trails
 * - Navigate between different functions
 */
const LoanManagementDemo = () => {
  const clients = useClients();
  const debtAccounts = useDebtAccounts();
  const paymentArrangements = usePaymentArrangements();
  const transactions = useTransactions();
  const [showArrangementForm, setShowArrangementForm] = useState(false);
const [editingArrangement, setEditingArrangement] = useState(null);
  // Application state
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeView, setActiveView] = useState('client'); // 'client', 'financial', 'transactions'
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [currentTheme, setCurrentTheme] = useState('blue'); // Default to blue theme

  // Create sample data on first load
  useEffect(() => {
    const createSampleData = async () => {
      console.log('useEffect triggered:', { isDataLoaded, clientsLength: clients.items.length });
      
      // Force creation if no clients exist, regardless of isDataLoaded flag
      if (clients.items.length > 0) {
        console.log('Clients already exist, skipping creation');
        setIsDataLoaded(true);
        return;
      }
      
      console.log('No clients found, forcing data creation...');

      try {
        console.log('Creating sample loan management data...');

        // Create multiple sample clients with diverse debt scenarios
        const sampleClients = [
          {
            clientName: 'John Smith',
            debtCode: 'CC01',
            homeAddress: '123 Main St, Anytown, ST 12345',
            phone: '(555) 123-4567',
            workPhone: '(555) 123-4568',
            cell: '(555) 123-4569',
            email: 'john.smith@email.com',
            contact: 'John Smith',
            ssn: '123-45-6789',
            debtAmount: 15000.00,
            payments: 3500.00,
            scenario: 'high_balance'
          },
          {
            clientName: 'Sarah Johnson',
            debtCode: 'CC02', 
            homeAddress: '456 Oak Ave, Springfield, IL 62701',
            phone: '(555) 987-6543',
            cell: '(555) 987-6544',
            email: 'sarah.johnson@email.com',
            contact: 'Sarah Johnson',
            ssn: '987-65-4321',
            debtAmount: 8500.00,
            payments: 2000.00,
            scenario: 'with_adjustment'
          },
          {
            clientName: 'Michael Davis',
            debtCode: 'PL01',
            homeAddress: '789 Pine Rd, Riverside, CA 92501',
            phone: '(555) 456-7890',
            workPhone: '(555) 456-7891',
            email: 'michael.davis@email.com',
            contact: 'Michael Davis',
            ssn: '456-78-9012',
            debtAmount: 22000.00,
            payments: 5500.00,
            scenario: 'large_debt'
          },
          {
            clientName: 'Emily Rodriguez',
            debtCode: 'AU01',
            homeAddress: '321 Elm Street, Austin, TX 78701',
            phone: '(555) 234-5678',
            cell: '(555) 234-5679',
            email: 'emily.rodriguez@email.com',
            contact: 'Emily Rodriguez',
            ssn: '234-56-7890',
            debtAmount: 18500.00,
            payments: 1200.00,
            scenario: 'recent_default'
          },
          {
            clientName: 'Robert Wilson',
            debtCode: 'MG01',
            homeAddress: '654 Maple Drive, Denver, CO 80202',
            phone: '(555) 345-6789',
            workPhone: '(555) 345-6790',
            fax: '(555) 345-6791',
            email: 'robert.wilson@email.com',
            contact: 'Robert Wilson',
            ssn: '345-67-8901',
            debtAmount: 45000.00,
            payments: 12000.00,
            scenario: 'mortgage_default'
          },
          {
            clientName: 'Lisa Chen',
            debtCode: 'ST01',
            homeAddress: '987 University Blvd, Boston, MA 02101',
            phone: '(555) 567-8901',
            cell: '(555) 567-8902',
            email: 'lisa.chen@email.com',
            contact: 'Lisa Chen',
            ssn: '567-89-0123',
            debtAmount: 35000.00,
            payments: 8500.00,
            scenario: 'student_loan'
          },
          {
            clientName: 'David Thompson',
            debtCode: 'CC03',
            homeAddress: '147 Broadway Ave, Seattle, WA 98101',
            phone: '(555) 678-9012',
            workPhone: '(555) 678-9013',
            email: 'david.thompson@email.com',
            contact: 'David Thompson',
            ssn: '678-90-1234',
            debtAmount: 5500.00,
            payments: 4200.00,
            scenario: 'nearly_paid'
          },
          {
            clientName: 'Amanda Foster',
            debtCode: 'PL02',
            homeAddress: '258 Cedar Lane, Miami, FL 33101',
            phone: '(555) 789-0123',
            cell: '(555) 789-0124',
            email: 'amanda.foster@email.com',
            contact: 'Amanda Foster',
            ssn: '789-01-2345',
            debtAmount: 12500.00,
            payments: 0.00,
            scenario: 'no_payments'
          }
        ];

        // Accumulate all items in arrays before adding to state
        const allClients = [];
        const allDebtAccounts = [];
        const allPaymentArrangements = [];
        const allTransactions = [];

        for (const clientData of sampleClients) {
          console.log(`\n=== Creating client: ${clientData.clientName} ===`);
          // Create client
          const client = createClient({
            clientName: clientData.clientName,
            debtCode: clientData.debtCode,
            homeAddress: clientData.homeAddress,
            phone: clientData.phone,
            workPhone: clientData.workPhone || '',
            cell: clientData.cell || '',
            fax: clientData.fax || '',
            email: clientData.email,
            contact: clientData.contact,
            ssn: clientData.ssn,
            acctRep: 'Agent Smith',
            alias: '',
            comment: `Sample client - ${clientData.scenario} scenario`
          });

          // Add ID manually
          client.id = Date.now() + Math.random();
          allClients.push(client);
          
          const clientId = client.id;
          console.log('Client ID for debt account:', clientId, 'Type:', typeof clientId);

          // Create debt account with scenario-specific details
          let debtAccountData = {
            clientId: String(clientId), // Ensure it's a string
            debtAmount: clientData.debtAmount,
            payments: clientData.payments
          };

          // Customize based on scenario
          switch (clientData.scenario) {
            case 'high_balance':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.05, // 5% legal fees
                interest: clientData.debtAmount * 0.12, // 12% interest
                debtFees: 350.00,
                lastPaymentDate: new Date('2024-01-15'),
                lastPaymentAmount: 500.00
              };
              break;

            case 'with_adjustment':
              debtAccountData = {
                ...debtAccountData,
                adjustments: -500.00, // Goodwill adjustment
                legalFees: clientData.debtAmount * 0.03,
                interest: clientData.debtAmount * 0.08,
                debtFees: 200.00,
                lastPaymentDate: new Date('2024-01-20'),
                lastPaymentAmount: 300.00
              };
              break;

            case 'large_debt':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.08, // Higher legal fees
                interest: clientData.debtAmount * 0.15, // Higher interest
                debtFees: 500.00,
                lastPaymentDate: new Date('2024-01-10'),
                lastPaymentAmount: 1000.00
              };
              break;

            case 'recent_default':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.06,
                interest: clientData.debtAmount * 0.18, // High interest for recent default
                debtFees: 400.00,
                lastPaymentDate: new Date('2023-11-15'), // Older payment
                lastPaymentAmount: 200.00
              };
              break;

            case 'mortgage_default':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.04, // Lower percentage but high amount
                interest: clientData.debtAmount * 0.06, // Lower mortgage rate
                debtFees: 750.00, // Higher fees for mortgage
                lastPaymentDate: new Date('2024-01-05'),
                lastPaymentAmount: 2500.00
              };
              break;

            case 'student_loan':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.02, // Lower legal fees
                interest: clientData.debtAmount * 0.07, // Student loan rate
                debtFees: 150.00,
                lastPaymentDate: new Date('2024-01-25'),
                lastPaymentAmount: 450.00
              };
              break;

            case 'nearly_paid':
              debtAccountData = {
                ...debtAccountData,
                adjustments: -100.00, // Small adjustment
                legalFees: clientData.debtAmount * 0.02,
                interest: clientData.debtAmount * 0.05,
                debtFees: 75.00,
                lastPaymentDate: new Date('2024-01-28'),
                lastPaymentAmount: 200.00
              };
              break;

            case 'no_payments':
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.10, // High legal fees
                interest: clientData.debtAmount * 0.20, // High interest
                debtFees: 600.00,
                lastPaymentDate: null,
                lastPaymentAmount: 0.00
              };
              break;

            default:
              debtAccountData = {
                ...debtAccountData,
                adjustments: 0.00,
                legalFees: clientData.debtAmount * 0.05,
                interest: clientData.debtAmount * 0.08,
                debtFees: 250.00,
                lastPaymentDate: new Date('2024-01-15'),
                lastPaymentAmount: 300.00
              };
          }

          console.log('Creating debt account with data:', debtAccountData);
          const debtAccount = createDebtAccount(debtAccountData);
          debtAccount.id = Date.now() + Math.random();
          allDebtAccounts.push(debtAccount);

          // Create payment arrangement for some clients based on scenario
          if (['high_balance', 'with_adjustment', 'recent_default', 'student_loan'].includes(clientData.scenario)) {
            let arrangementData = {
              clientId: clientId,
              isActive: true
            };

            switch (clientData.scenario) {
              case 'high_balance':
                arrangementData = {
                  ...arrangementData,
                  frequency: PAYMENT_FREQUENCIES.MONTHLY,
                  amount: 400.00,
                  nextDueDate: new Date('2024-02-15'),
                  settleForLess: true,
                  settleAmount: 9000.00, // 60% settlement
                  arrears: 0.00
                };
                break;

              case 'with_adjustment':
                arrangementData = {
                  ...arrangementData,
                  frequency: PAYMENT_FREQUENCIES.BI_WEEKLY,
                  amount: 200.00,
                  nextDueDate: new Date('2024-02-10'),
                  settleForLess: false,
                  settleAmount: 0.00,
                  arrears: 0.00
                };
                break;

              case 'recent_default':
                arrangementData = {
                  ...arrangementData,
                  frequency: PAYMENT_FREQUENCIES.WEEKLY,
                  amount: 150.00,
                  nextDueDate: new Date('2024-02-05'),
                  settleForLess: true,
                  settleAmount: 12000.00, // 65% settlement
                  arrears: 300.00 // Has some arrears
                };
                break;

              case 'student_loan':
                arrangementData = {
                  ...arrangementData,
                  frequency: PAYMENT_FREQUENCIES.MONTHLY,
                  amount: 350.00,
                  nextDueDate: new Date('2024-02-20'),
                  settleForLess: false,
                  settleAmount: 0.00,
                  arrears: 0.00
                };
                break;
            }

            const arrangement = createPaymentArrangement(arrangementData);
            arrangement.id = 'arrangement_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            allPaymentArrangements.push(arrangement);
          }

          // Create scenario-specific transaction history
          let transactionHistory = [];

          switch (clientData.scenario) {
            case 'high_balance':
              transactionHistory = [
                createPaymentTransaction(clientId, 500.00, 'Payment received - Check #1234', 'CHK1234', 'agent_smith'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Discussed payment arrangement options', 'agent_jones'),
                createPaymentTransaction(clientId, 750.00, 'Payment received - Online transfer', 'TXN78901', 'system'),
                createPaymentTransaction(clientId, 2250.00, 'Large payment - Money order', 'MO5678', 'agent_brown')
              ];
              break;

            case 'with_adjustment':
              transactionHistory = [
                createAdjustmentTransaction(clientId, -500.00, 'Goodwill adjustment - client hardship', 'supervisor_wilson'),
                createPaymentTransaction(clientId, 300.00, 'Payment received - Check #2345', 'CHK2345', 'agent_davis'),
                createContactTransaction(clientId, CONTACT_TYPES.EMAIL, 'Sent payment confirmation and new balance', 'system'),
                createPaymentTransaction(clientId, 1700.00, 'Payment received - Bank transfer', 'TXN12345', 'agent_smith')
              ];
              break;

            case 'large_debt':
              transactionHistory = [
                createPaymentTransaction(clientId, 1000.00, 'Initial payment - Cashier check', 'CC9876', 'agent_johnson'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Negotiated payment plan for large balance', 'agent_smith'),
                createPaymentTransaction(clientId, 2000.00, 'Payment received - Wire transfer', 'WIRE001', 'system'),
                createPaymentTransaction(clientId, 2500.00, 'Payment received - Check #3456', 'CHK3456', 'agent_brown')
              ];
              break;

            case 'recent_default':
              transactionHistory = [
                createContactTransaction(clientId, CONTACT_TYPES.LETTER, 'Sent initial demand letter', 'agent_brown'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Left voicemail - no response', 'agent_davis'),
                createPaymentTransaction(clientId, 200.00, 'Small payment received - Check #4567', 'CHK4567', 'agent_smith'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Spoke with client - agreed to weekly payments', 'agent_jones'),
                createPaymentTransaction(clientId, 1000.00, 'Payment received - Online payment', 'ONL9876', 'system')
              ];
              break;

            case 'mortgage_default':
              transactionHistory = [
                createPaymentTransaction(clientId, 2500.00, 'Partial mortgage payment - Check #5678', 'CHK5678', 'agent_wilson'),
                createContactTransaction(clientId, CONTACT_TYPES.IN_PERSON, 'Met with client at property - discussed options', 'agent_johnson'),
                createPaymentTransaction(clientId, 5000.00, 'Large payment - Cashier check', 'CC1234', 'agent_smith'),
                createPaymentTransaction(clientId, 4500.00, 'Payment received - Wire transfer', 'WIRE002', 'system')
              ];
              break;

            case 'student_loan':
              transactionHistory = [
                createPaymentTransaction(clientId, 450.00, 'Monthly payment - Auto debit', 'AUTO001', 'system'),
                createPaymentTransaction(clientId, 450.00, 'Monthly payment - Auto debit', 'AUTO002', 'system'),
                createContactTransaction(clientId, CONTACT_TYPES.EMAIL, 'Sent payment schedule and balance update', 'system'),
                createPaymentTransaction(clientId, 2000.00, 'Large payment - Tax refund', 'CHK6789', 'agent_davis'),
                createPaymentTransaction(clientId, 5600.00, 'Bulk payment - Check #7890', 'CHK7890', 'agent_smith')
              ];
              break;

            case 'nearly_paid':
              transactionHistory = [
                createPaymentTransaction(clientId, 1000.00, 'Payment received - Check #8901', 'CHK8901', 'agent_brown'),
                createPaymentTransaction(clientId, 1500.00, 'Payment received - Online payment', 'ONL5432', 'system'),
                createAdjustmentTransaction(clientId, -100.00, 'Small goodwill adjustment', 'agent_davis'),
                createPaymentTransaction(clientId, 1700.00, 'Final large payment - Check #9012', 'CHK9012', 'agent_smith')
              ];
              break;

            case 'no_payments':
              transactionHistory = [
                createContactTransaction(clientId, CONTACT_TYPES.LETTER, 'Initial demand letter sent via certified mail', 'agent_brown'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Multiple call attempts - no answer', 'agent_davis'),
                createContactTransaction(clientId, CONTACT_TYPES.EMAIL, 'Sent email notice - bounced back', 'system'),
                createContactTransaction(clientId, CONTACT_TYPES.LETTER, 'Final demand letter sent', 'agent_wilson')
              ];
              break;

            default:
              transactionHistory = [
                createPaymentTransaction(clientId, clientData.payments * 0.4, 'Payment received - Check', `CHK${Math.floor(Math.random() * 10000)}`, 'agent_smith'),
                createContactTransaction(clientId, CONTACT_TYPES.PHONE_CALL, 'Spoke with client about payment options', 'agent_jones'),
                createPaymentTransaction(clientId, clientData.payments * 0.6, 'Payment received - Online transfer', `TXN${Math.floor(Math.random() * 100000)}`, 'system')
              ];
          }

          // Add timestamps and IDs to transactions
          transactionHistory.forEach((txn, index) => {
            txn.timestamp = new Date(Date.now() - (index + 1) * 7 * 24 * 60 * 60 * 1000); // Weekly intervals
            txn.id = 'txn_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '_' + index;
            allTransactions.push(txn);
          });
        }

        // Bulk load all data into hooks using setItems
        console.log('Bulk loading all data into hooks...');
        console.log(`Created: ${allClients.length} clients, ${allDebtAccounts.length} debt accounts, ${allPaymentArrangements.length} arrangements, ${allTransactions.length} transactions`);
        
        clients.setItems(allClients);
        debtAccounts.setItems(allDebtAccounts);
        paymentArrangements.setItems(allPaymentArrangements);
        transactions.setItems(allTransactions);
        
        // Save to localStorage after a delay to ensure state has updated
        setTimeout(() => {
          console.log('Saving all data to localStorage...');
          localStorage.setItem(STORAGE_KEYS.CLIENTS, JSON.stringify(allClients));
          localStorage.setItem(STORAGE_KEYS.DEBT_ACCOUNTS, JSON.stringify(allDebtAccounts));
          localStorage.setItem(STORAGE_KEYS.PAYMENT_ARRANGEMENTS, JSON.stringify(allPaymentArrangements));
          localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(allTransactions));
          console.log('All data saved to localStorage');
        }, 100);

        setIsDataLoaded(true);
        
        // Force a small delay to ensure state updates
        setTimeout(() => {
          console.log('Final clients after data creation:', clients.items);
          console.log('Final clients detailed:', clients.items.map(c => ({ id: c.id, name: c.clientName, code: c.debtCode })));
          // Force update available clients
          setAvailableClients([...clients.items]);
        }, 100);
        
        console.log('Sample data created successfully');

      } catch (error) {
        console.error('Error creating sample data:', error);
      }
    };

    createSampleData();
  }, [clients, debtAccounts, paymentArrangements, transactions, isDataLoaded]);

  // Fallback: Create a simple test client if no clients exist after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clients.items.length === 0 && !isDataLoaded) {
        console.log('No clients found, creating simple test client...');
        const testClient = createClient({
          clientName: 'Test Client',
          debtCode: 'TEST001',
          homeAddress: '123 Test St',
          phone: '(555) 000-0000',
          email: 'test@example.com'
        });
        
        clients.create(testClient).then(result => {
          console.log('Test client creation result:', result);
          if (result.success) {
            setIsDataLoaded(true);
          }
        });
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [clients, isDataLoaded]);

  // Update available clients when clients change
  useEffect(() => {
    console.log('Updating available clients:', { 
      clientsItems: clients.items, 
      clientsLength: clients.items.length,
      clientsItemsDetailed: clients.items.map(c => ({ id: c.id, name: c.clientName, code: c.debtCode }))
    });
    
    setAvailableClients(clients.items);
    
    // If we have a selectedClientId but no selectedClient, try to restore it
    if (selectedClientId && !selectedClient && clients.items.length > 0) {
      const client = clients.getById(selectedClientId);
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [clients.items, selectedClientId, selectedClient, clients]);

  // Handle client selection
  const handleClientSelected = (client) => {
    setSelectedClient(client);
    setSelectedClientId(client.id);
  };

  // Handle client selection from dropdown
  const handleClientDropdownChange = (value) => {
    console.log('Dropdown change received:', value);
    
    // Handle different possible value formats from React95 Select
    let clientId;
    if (typeof value === 'string') {
      clientId = value;
    } else if (value && typeof value === 'object') {
      clientId = value.value || value;
    } else {
      clientId = value;
    }
    
    console.log('Processed clientId:', clientId);
    
    if (clientId && clientId !== '' && clientId !== selectedClientId) {
      const client = clients.getById(clientId);
      console.log('Found client:', client);
      if (client) {
        setSelectedClient(client);
        setSelectedClientId(clientId);
        console.log('Client selected successfully');
      }
    } else if (!clientId || clientId === '') {
      // Clear selection
      setSelectedClient(null);
      setSelectedClientId(null);
      console.log('Selection cleared');
    }
  };

  // Handle view changes
  const handleViewChange = (view) => {
    setActiveView(view);
  };

  // Handle payment processed
  const handlePaymentProcessed = (paymentInfo) => {
    console.log('Payment processed:', paymentInfo);
    // The components will automatically update due to hook dependencies
  };

  // Handle transaction added
  const handleTransactionAdded = (transaction) => {
    console.log('Transaction added:', transaction);
    // The components will automatically update due to hook dependencies
  };

  // Toggle theme between blue and original
  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'blue' ? 'original' : 'blue');
  };

  // Get current theme object
  const getTheme = () => {
    return currentTheme === 'blue' ? blue : original;
  };
  const resetSampleData = () => {
    console.log('Resetting all sample data...');
    // Clear only loan management localStorage keys (preserves other data)
    const keysToRemove = [
      'loan_management_clients',
      'loan_management_debt_accounts', 
      'loan_management_payment_arrangements',
      'loan_management_transactions',
      'loan_management_settings'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('Removed localStorage key:', key);
    });
    
    // Reset all state immediately
    setIsDataLoaded(false);
    setAvailableClients([]);
    setSelectedClientId('');
    setSelectedClient(null);
    setActiveView('client');
    
    console.log('State reset, reloading page...');
    // Force recreation after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Handle creating a new payment arrangement
const handleCreateArrangement = () => {
  if (!selectedClientId) {
    alert('Please select a client first');
    return;
  }
  setEditingArrangement(null); // Clear any existing arrangement
  setShowArrangementForm(true);
};

// Handle editing an existing arrangement
const handleEditArrangement = (arrangement) => {
  setEditingArrangement(arrangement);
  setShowArrangementForm(true);
};

// Handle arrangement saved
const handleArrangementSaved = async (arrangement) => {
  console.log('Arrangement saved:', arrangement);
  
  // Create a transaction record for the arrangement
  const arrangementTransaction = createArrangementTransaction(
    arrangement.clientId,
    `Payment arrangement ${editingArrangement ? 'updated' : 'created'}: ${arrangement.frequency} payments of $${arrangement.amount}`,
    arrangement.id
  );
  
  await transactions.create(arrangementTransaction);
  
  // Close the form
  setShowArrangementForm(false);
  setEditingArrangement(null);
  
  // Show success message (optional)
  alert(`Payment arrangement ${editingArrangement ? 'updated' : 'created'} successfully!`);
};

// Handle form close
const handleArrangementFormClose = () => {
  setShowArrangementForm(false);
  setEditingArrangement(null);
};
  // Render main content based on active view
  const renderMainContent = () => {
    switch (activeView) {
      case 'financial':
        return (
          // <FinancialSummary 
          //   clientId={selectedClientId}
          //   onPaymentRecorded={handlePaymentProcessed}
          //   showDetailedView={true}
          //   allClients={clients.items}
          // />
          <div>
              <FinancialSummary 
                clientId={selectedClientId}
                onPaymentRecorded={handlePaymentProcessed}
                onArrangementEdit={handleEditArrangement}  // ADD THIS LINE
                showDetailedView={true}
                allClients={clients.items}
              />
              
              {/* Add Arrangement Management Button */}
              <div style={{ 
                position: 'fixed', 
                bottom: '100px', 
                right: '400px', 
                zIndex: 999 
              }}>
                <Button 
                  onClick={handleCreateArrangement}
                  style={{ 
                    padding: '10px 20px',
                    fontSize: '12px',
                    backgroundColor: '#006600',
                    color: 'white'
                  }}
                >
                  ➕ New Payment Arrangement
                </Button>
              </div>
            </div>
        );
      
      case 'transactions':
        return (
          <TransactionHistory 
            clientId={selectedClientId}
            onTransactionAdded={handleTransactionAdded}
            onPaymentProcessed={handlePaymentProcessed}
            showAddTransaction={true}
          />
        );
      
      default:
        return (
          <ClientDetailsForm 
            initialClient={selectedClient}
            onClientSelected={handleClientSelected}
            onClientSaved={(client) => {
              console.log('Client saved:', client);
              setSelectedClient(client);
            }}
            showFinancialSummary={true}
          />
        );
    }
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <div style={{ 
        padding: '10px', 
        backgroundColor: '#c0c0c0', 
        minHeight: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'auto',
        fontFamily: 'MS Sans Serif, sans-serif'
      }}>
        
        {/* Application Header */}
        <Window style={{ marginBottom: '10px',marginLeft: '180px' }}>
          <WindowHeader>
            <span>🏦 Loan Management System - Integrated Demo</span>
          </WindowHeader>
          <WindowContent style={{ padding: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                  Select Client:
                </label>
                <Select
                  value={selectedClientId || ''}
                  onChange={handleClientDropdownChange}
                  options={(() => {
                    const options = [
                      { value: '', label: 'Choose a client...' },
                      ...availableClients.map(client => ({
                        value: client.id,
                        label: `${client.clientName} (${client.debtCode})`
                      }))
                    ];
                    console.log('Dropdown options:', options);
                    console.log('Available clients for dropdown:', availableClients);
                    console.log('Clients.items for comparison:', clients.items);
                    return options;
                  })()}
                  style={{ width: '250px' }}
                />
              </div>
              
              {/* Reset Sample Data Button */}
              <div>
                {/* <Button
                  onClick={resetSampleData}
                  style={{ 
                    fontSize: '11px',
                    padding: '4px 8px',
                    backgroundColor: '#ff6b6b',
                    color: 'white'
                  }}
                >
                  🔄 Reset Sample Data
                </Button> */}
              </div>
              
              {/* Force Create Data Button (for debugging) */}
              {/* <div>
                <Button
                  onClick={() => {
                    console.log('Force creating data...');
                    setIsDataLoaded(false);
                    // Trigger useEffect by changing state
                    setTimeout(() => {
                      console.log('Triggering data creation...');
                    }, 100);
                  }}
                  style={{ 
                    fontSize: '11px',
                    padding: '4px 8px',
                    backgroundColor: '#4CAF50',
                    color: 'white'
                  }}
                >
                  ⚡ Force Create Data
                </Button>
              </div> */}
              
              {/* Theme Toggle Button */}
              <div>
                <Button
                  onClick={toggleTheme}
                  style={{ 
                    fontSize: '11px',
                    padding: '4px 8px',
                    backgroundColor: currentTheme === 'blue' ? '#4169E1' : '#008080',
                    color: 'white'
                  }}
                >
                  🎨 {currentTheme === 'blue' ? 'Blue' : 'Original'}
                </Button>
              </div>
              
              {selectedClient && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>Selected:</strong> {selectedClient.clientName}<br/>
                  <strong>Phone:</strong> {selectedClient.phone}
                </div>
              )}
              
              {/* Debug info */}
              <div style={{ fontSize: '10px', color: '#999', marginLeft: '20px' }}>
                Debug: {availableClients.length} clients available, 
                {clients.items.length} in hook, 
                Data loaded: {isDataLoaded ? 'Yes' : 'No'}
              </div>
            </div>
          </WindowContent>
        </Window>

        <div style={{ display: 'flex', gap: '10px', height: 'calc(100vh - 120px)' }}>
          
          {/* Navigation Panel */}
          <div style={{ width: '200px' }}>
            {/* View Selection */}
            <Window>
              <WindowHeader>
                <span>📋 Views</span>
              </WindowHeader>
              <WindowContent style={{ padding: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <Button 
                    onClick={() => handleViewChange('client')}
                    style={{ 
                      backgroundColor: activeView === 'client' ? '#0066cc' : undefined,
                      color: activeView === 'client' ? 'white' : undefined
                    }}
                  >
                    👤 Client Details
                  </Button>
                  <Button 
                    onClick={() => handleViewChange('financial')}
                    disabled={!selectedClientId}
                    style={{ 
                      backgroundColor: activeView === 'financial' ? '#0066cc' : undefined,
                      color: activeView === 'financial' ? 'white' : undefined,
                      fontSize: '12px'
                    }}
                  >
                    💰 Financial Summary
                  </Button>
                  <Button 
                    onClick={() => handleViewChange('transactions')}
                    disabled={!selectedClientId}
                    style={{ 
                      backgroundColor: activeView === 'transactions' ? '#0066cc' : undefined,
                      color: activeView === 'transactions' ? 'white' : undefined
                    }}
                  >
                    📋 Transactions
                  </Button>
                  <Button 
                    onClick={handleCreateArrangement}
                    disabled={!selectedClientId}
                    style={{ 
                      fontSize: '12px',
                      backgroundColor: '#006600',
                      color: 'white'
                    }}
                  >
                    📅 Payment Arrangement
                  </Button>
                </div>
              </WindowContent>
            </Window>
            
            <NavigatorPanel 
              currentClient={selectedClient}
              onNavigate={(navigationData) => {
                console.log('Navigate to:', navigationData);
                // Handle object format from NavigatorPanel
                const section = typeof navigationData === 'string' 
                  ? navigationData 
                  : navigationData.section || navigationData.function || '';
                
                // Map navigation to views
                if (section.includes('financial') || section.includes('balance')) {
                  handleViewChange('financial');
                } else if (section.includes('transaction') || section.includes('payment')) {
                  handleViewChange('transactions');
                } else {
                  handleViewChange('client');
                }
              }}
              style={{ marginTop: '10px' }}
            />
          </div>

          {/* Main Content Area */}
          <div style={{ 
            flex: 1, 
            backgroundColor: '#c0c0c0' 
          }}>
            {renderMainContent()}
          </div>
        </div>

        {/* Instructions Panel - Bottom Right */}
        <Window style={{ 
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '350px',
          zIndex: 1000
        }}>
          <WindowHeader>
            <span>💡 Demo Instructions</span>
          </WindowHeader>
          <WindowContent style={{ padding: '10px' }}>
            <div style={{ fontSize: '11px' }}>
              <strong>How to Test Payment Processing:</strong>
              <ol style={{ marginLeft: '20px', marginTop: '5px' }}>
                <li>Select a client from the dropdown above</li>
                <li>Click "💰 Financial Summary" to see current balances</li>
                <li>Click "📋 Transactions" to view transaction history</li>
                <li>In Transactions view, click "➕ Add Transaction"</li>
                <li>Select "Payment" type, enter amount and details</li>
                <li>Submit and watch the balance update in real-time!</li>
                <li>Switch between views to see updates across all components</li>
              </ol>
              
              <div style={{ marginTop: '10px' }}>
                <strong>Features Available:</strong>
                • Real-time balance calculations • Complete audit trails • Payment processing • 
                Contact logging • Account adjustments • Transaction filtering & search • 
                Payment arrangement tracking
              </div>
            </div>
          </WindowContent>
        </Window>
      </div>
      {/* Payment Arrangement Form Modal */}
      {showArrangementForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <PaymentArrangementForm
            clientId={selectedClientId}
            initialArrangement={editingArrangement}
            onArrangementSaved={handleArrangementSaved}
            onClose={handleArrangementFormClose}
          />
        </div>
      )}
    </ThemeProvider>
  );
};

export default LoanManagementDemo;